import { Injectable } from '@angular/core';
import { Asset } from 'app/models/document';
import { Util } from 'app/models/util';
import { PermissionsService } from 'app/modules/admin/apps/file-manager/permissions/permissions.service';
import { LpmConfig } from 'app/models/lpm.config';
import { ActionDefinition, AppActionId, AppView } from 'app/shared/action.types';

// Views where lpmConfig flags apply.
const CONFIG_GATED_VIEWS: AppView[] = ['shared'];

// Central action map for appAction.
@Injectable({ providedIn: 'root' })
export class ActionRegistryService {
    private readonly definitions: Record<AppActionId, ActionDefinition>;
    constructor(private permissionsService: PermissionsService, private util: Util) {
        const isLockedForMe = (asset: Asset) => this.util.isLockedForCurrentUser(asset);
        const lockedReason = (asset: Asset) => {
            const name = `${asset?.lockDetail?.firstName ?? ''} ${asset?.lockDetail?.lastName ?? ''}`.trim();
            return name ? `Locked by ${name}` : 'This item is locked';
        };
        // Hidden when every flag is off in a gated view.
        const flagsOff = (view: AppView, ...flags: (keyof LpmConfig)[]) => {
            const lpm = this.util.getWebComponentInputParams()?.lpmConfig;
            return CONFIG_GATED_VIEWS.includes(view) && flags.every(flag => lpm?.[flag] === false);
        };

        this.definitions = {
            view: {
                hide: (asset: Asset) => !this.permissionsService.canView(asset),
            },
            edit: {
                hide: (asset: Asset) => !this.permissionsService.canEdit(asset) || isLockedForMe(asset),
                deniedReason: lockedReason,
            },
            download: {
                hide: (asset: Asset) => asset.type !== 'FILE',
                disable: (asset: Asset) => isLockedForMe(asset),
                deniedReason: lockedReason,
            },
            share: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'allowShare'),
                disable: (asset: Asset) => !this.permissionsService.canShare(asset) || isLockedForMe(asset),
                deniedReason: lockedReason,
            },
            copy: {
                disable: (asset: Asset) => isLockedForMe(asset),
                deniedReason: lockedReason,
            },
            move: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'allowMove'),
                disable: (asset: Asset) => !this.permissionsService.canMove(asset) || isLockedForMe(asset),
                deniedReason: lockedReason,
            },
            rename: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'allowRename'),
                disable: (asset: Asset) => isLockedForMe(asset) || asset.accessLevel === 'VIEWER',
                deniedReason: (asset: Asset) => (isLockedForMe(asset) ? lockedReason(asset) : 'You only have view access'),
            },
            delete: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'allowTrash'),
                disable: (asset: Asset) => !this.permissionsService.canDelete(asset) || isLockedForMe(asset),
                deniedReason: (asset: Asset) => (isLockedForMe(asset) ? lockedReason(asset) : 'This item cannot be deleted'),
            },
            copilot: {
                disable: (asset: Asset) => isLockedForMe(asset) || asset.parsingProgress !== 'Completed',
                deniedReason: (asset: Asset) => (isLockedForMe(asset) ? lockedReason(asset) : 'File is still being processed'),
            },
            openOnDesktop: {
                hide: (asset: Asset) => !this.permissionsService.isOpenOnDesktopVisible(asset),
                disable: (asset: Asset) => isLockedForMe(asset),
                deniedReason: lockedReason,
            },
            desktopAgentBanner: {
                hide: () => !this.permissionsService.isOpenOnDesktopEnabled(),
            },
            lock: {
                hide: (asset: Asset, view?: AppView) => !this.util.isCheckedIn(asset) || flagsOff(view, 'allowLock'),
            },
            unlock: {
                hide: (asset: Asset) => !this.util.isCheckedOut(asset),
            },
            requestAccess: {
                hide: (asset: Asset, view?: AppView) => !this.util.isRequestable(asset) || flagsOff(view, 'allowLock'),
            },
            favorite: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'organizeFavorite'),
            },
            label: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'organizeLabel'),
            },
            organize: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'allowMove', 'organizeFavorite', 'organizeLabel'),
            },
            details: {
                hide: (_asset: Asset, view?: AppView) =>
                    flagsOff(view, 'viewInfoDetails', 'viewActivityDetails', 'viewNotesDetails', 'viewPermissions'),
            },
            detailsInfo: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'viewInfoDetails'),
            },
            detailsActivity: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'viewActivityDetails'),
            },
            detailsNotes: {
                hide: (_asset: Asset, view?: AppView) => flagsOff(view, 'viewNotesDetails'),
            },
            filterByLabel: {
                hide: (_item: any, view?: AppView) => flagsOff(view, 'filterByLabel'),
            },
            filterByTrash: {
                hide: (_item: any, view?: AppView) => flagsOff(view, 'filterByTrash'),
            },
            filterByFavorite: {
                hide: (_item: any, view?: AppView) => flagsOff(view, 'filterByFavorite'),
            },
        };
    }

    resolve(action: AppActionId): ActionDefinition | undefined {
        return this.definitions[action];
    }

    // Allowed when neither hidden nor disabled.
    isAllowed(action: AppActionId, asset: Asset): boolean {
        const definition = this.definitions[action];
        return !!definition && !definition.hide?.(asset) && !definition.disable?.(asset);
    }

    // Same hide check, for component code.
    isHidden(action: AppActionId, item: any, view?: AppView): boolean {
        return this.resolve(action)?.hide?.(item, view) ?? false;
    }

    // Structural usage also removes disabled actions.
    isRemoved(action: AppActionId, item: any, view?: AppView): boolean {
        return this.isHidden(action, item, view) || (this.resolve(action)?.disable?.(item, view) ?? false);
    }

}
